import { subtractDateTime } from '@/_utils/subtract-date-time';
import {
	BagPaymentOrganization,
	BagPaymentTermCurrency,
	BagPaymentTermFilters,
	BagPaymentTermListParseFilters,
	BagPaymentTermParametersStatus,
	BagPaymentTermProduct,
	CountKind,
	FastFilter,
	ReservationStatus,
} from '@/bag/bag-payment-term-list/bag-payment-term-list.model';
import { Seller } from '@/common/enums/Seller';
import { FastFiltersComponent } from '@/common/fast-filters/fast-filters.component';
import { DatePipe } from '@angular/common';
import { Component, Input, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Data2Service } from '@/common/data2.service';
import { DateService } from '@/common/date.service';
import {
	CancellationStatusesListReservation,
	PaymentStatuses,
	RejectedStatusesListReservation,
	Status,
} from '@/common/enums/Status';
import { FiltersComponent } from '@/common/filters.component';

@Component({
	selector: 'app-bag-payment-term-list-filters',
	templateUrl: './bag-payment-term-list-filters.component.html',
	styleUrls: ['./bag-payment-term-list-filters.component.scss'],
	providers: [DatePipe],
})
export class BagPaymentTermListFiltersComponent extends FiltersComponent {
	@Input() countKind: CountKind[];
	@Input() currency: BagPaymentTermCurrency[];
	@Input() status: BagPaymentTermParametersStatus[];
	@Input() reservationStatus: ReservationStatus[];
	@Input() organizations: BagPaymentOrganization[];
	@Input() agencies: BagPaymentOrganization[];
	@Input() products: BagPaymentTermProduct[];

	fastFilterComponent = viewChild<FastFiltersComponent>('fastFilter');

	constructor(
		protected api: Data2Service,
		protected route: ActivatedRoute,
		protected router: Router,
		protected date: DateService,
		protected datePipe: DatePipe,
	) {
		super(api, route, router);
	}

	parseFilters: BagPaymentTermListParseFilters = {
		reservation_status: [],
		term__gte: this.datePipe.transform(new Date(), 'yyyy-MM-dd'),
		term__lte: this.datePipe.transform(subtractDateTime(new Date(), { days: -5 }), 'yyyy-MM-dd'),
		value__gte: null,
		value__lt: null,
		reservation_stay_time_lower_after: null,
		reservation_stay_time_lower_before: null,
		reservation_stay_time_upper_after: null,
		reservation_stay_time_upper_before: null,
	};

	filters: BagPaymentTermFilters = this.defaultFilters();

	dateConvert: Array<string> = [
		'term__gte',
		'term__lte',
		'reservation_stay_time_lower_after',
		'reservation_stay_time_lower_before',
		'reservation_stay_time_upper_after',
		'reservation_stay_time_upper_before',
	];

	fastFilters: FastFilter[] = [
		{
			id: 0,
			name: 'Rezerwacje potwierdzone +5 dni (częściowo zapłacono, nie zapłacono)',
			filters: {
				dateFrom: new Date(),
				dateTo: subtractDateTime(new Date(), { days: -5 }),
				reservationStatus: [Status.CONFIRMED],
				paymentStatuses: [PaymentStatuses.PARTIALY_PAID, PaymentStatuses.NOT_PAID],
			},
		},
		{
			id: 1,
			name: 'Wszystkie rezerwacje anulowane (częściowo zapłacono, nie zapłacono)',
			filters: {
				reservationStatus: [Status.CANCELED],
				paymentStatuses: [PaymentStatuses.PARTIALY_PAID, PaymentStatuses.NOT_PAID],
			},
		},
		{
			id: 2,
			name: 'Rezerwacje potwierdzone ze świadczeniem od dziś + 5 dni (częsciowo zapłacone, nie zapłacone)',
			filters: {
				reservationStayTimeLowerBefore: subtractDateTime(new Date(), { days: -5 }),
				reservationStayTimeLowerAfter: new Date(),
				reservationStatus: [Status.CONFIRMED],
				paymentStatuses: [PaymentStatuses.PARTIALY_PAID, PaymentStatuses.NOT_PAID],
			},
		},
	];

	protected readonly Seller = Seller;

	protected filtersDefinition = {
		offer_product: {
			type: 'string',
			isArray: true,
		},
		currency__in: {
			type: 'string',
			isArray: true,
		},
		reservation_status: {
			type: 'string',
			isArray: true,
		},
		reservation_seller: {
			type: 'string',
			isArray: true,
		},
		reservation_organization: {
			type: 'string',
			isArray: true,
		},
		status__in: {
			type: 'string',
			isArray: true,
		},
		seller_is_oskar: {
			type: 'string',
			isArray: false,
		},
	};

	selectFastFilter(fastFilter: FastFilter): void {
		if (!fastFilter || !fastFilter.filters) {
			return;
		}

		this.filters = this.defaultFilters();

		if (fastFilter.filters.reservationStayTimeLowerBefore) {
			this.filters.reservation_stay_time_lower_before = this.datePipe.transform(
				fastFilter.filters.reservationStayTimeLowerBefore as Date,
				'yyyy-MM-dd',
			);
		}

		if (fastFilter.filters.reservationStayTimeLowerAfter) {
			this.filters.reservation_stay_time_lower_after = this.datePipe.transform(
				fastFilter.filters.reservationStayTimeLowerAfter as Date,
				'yyyy-MM-dd',
			);
		}

		if (fastFilter.filters.dateFrom) {
			this.filters.term__gte = this.datePipe.transform(fastFilter.filters.dateFrom as Date, 'yyyy-MM-dd');
		}

		if (fastFilter.filters.dateTo) {
			this.filters.term__lte = this.datePipe.transform(fastFilter.filters.dateTo as Date, 'yyyy-MM-dd');
		}

		if (fastFilter.filters.paymentStatuses) {
			this.filters.status__in = fastFilter.filters.paymentStatuses as string[];
		}

		if (fastFilter.filters.reservationStatus) {
			this.parseFilters.reservation_status = fastFilter.filters.reservationStatus as Status[];
		}

		this.checkStatusFilter();
	}

	checkStatusFilter(): void {
		this.filters.reservation_status = [];
		this.parseFilters.reservation_status?.forEach((obj) => {
			switch (obj) {
				case Status.CANCELED:
					this.filters.reservation_status.push(...CancellationStatusesListReservation);
					break;
				case Status.REJECTED:
					this.filters.reservation_status.push(...RejectedStatusesListReservation);
					break;
				case Status.OPTION:
					this.filters.reservation_status.push(Status.OPTION);
					break;
				case Status.CONFIRMED:
					this.filters.reservation_status.push(Status.CONFIRMED);
					break;
				default:
					this.filters.reservation_status = [];
					break;
			}
		});
		this.filters2url(this.filters);
	}
	filtersSave(): void {
		this.convertSaveFilters();
		this.filters2url(this.filters);
		this.api.put('/userFilters/' + this.userFilters, this.filters, (result, error) => {});
	}

	filtersClear(): void {
		for (const key in this.filters) {
			if (Array.isArray(this.filters[key])) {
				this.filters[key] = [];
				continue;
			}
			this.filters[key] = null;
		}

		for (const key in this.parseFilters) {
			if (Array.isArray(this.parseFilters[key])) {
				this.parseFilters[key] = [];
				continue;
			}
			this.parseFilters[key] = null;
		}

		this.fastFilterComponent().clear();
	}

	protected convertSaveFilters(): void {
		this.filters.table_name__in = 'reservation_reservation';
		Object.keys(this.filters).forEach((key: string) => {
			if (this.filters[key] === '' && !this.filters[key]) {
				this.filters[key] = null;
			}
			if (!Array.isArray(this.filters[key]) && this.parseFilters[key] && !this.dateConvert.includes(key)) {
				this.filters[key] = (this.parseFilters[key] * 100).toFixed(0);
			}
			if (this.dateConvert.includes(key) && this.parseFilters[key]) {
				this.filters[key] = this.datePipe.transform(this.parseFilters[key], 'yyyy-MM-dd');
			}
		});
		Object.keys(this.parseFilters).forEach((key) => {
			if (!this.parseFilters[key]) this.filters[key] = null;
		});
		this.checkStatusFilter();
	}

	protected filtersAfterLoad(): void {
		this.parseFilters.term__gte = this.filters.term__gte;
		this.parseFilters.term__lte = this.filters.term__lte;

		this.parseFilters.reservation_stay_time_lower_after = this.filters.reservation_stay_time_lower_after;
		this.parseFilters.reservation_stay_time_lower_before = this.filters.reservation_stay_time_lower_before;
		this.parseFilters.reservation_stay_time_upper_after = this.filters.reservation_stay_time_upper_after;
		this.parseFilters.reservation_stay_time_upper_before = this.filters.reservation_stay_time_upper_before;

		this.parseFilters.value__gte = this.filters.value__gte ? (this.filters.value__gte * 0.01).toFixed(2) : null;
		this.parseFilters.value__lt = this.filters.value__lt ? (this.filters.value__lt * 0.01).toFixed(2) : null;
		this.filters.reservation_status?.forEach((obj) => {
			switch (obj) {
				case Status.CANCELED:
					this.parseFilters.reservation_status.push(Status.CANCELED);
					break;
				case Status.REJECTED_EXPIRATION:
					this.parseFilters.reservation_status.push(Status.REJECTED);
					break;
				case Status.OPTION:
					this.parseFilters.reservation_status.push(Status.OPTION);
					break;
				case Status.CONFIRMED:
					this.parseFilters.reservation_status.push(Status.CONFIRMED);
					break;
			}
		});
	}

	protected filters2url(filters: BagPaymentTermFilters): void {
		if (!filters || filters instanceof Array) {
			this.selected.emit({});
			return;
		}
		filters.on = 1; // hack when no filters selected
		this.router.navigate(['.'], {
			relativeTo: this.route,
			queryParams: filters,
			preserveFragment: true,
		});
	}

	private defaultFilters(): BagPaymentTermFilters {
		return {
			reservation_status: [],
			reservation_seller: [],
			reservation_organization: [],
			count_kind: null,
			status__in: [],
			currency__in: [],
			table_id__in: null,
			table_name__in: null,
			term__gte: null,
			term__lte: null,
			value__gte: null,
			value__lt: null,
			seller_is_oskar: '',
			offer_product: [],
			reservation_stay_time_lower_after: null,
			reservation_stay_time_lower_before: null,
			reservation_stay_time_upper_after: null,
			reservation_stay_time_upper_before: null,
			source: null,
		};
	}
}
